import { useParams, NavLink } from 'react-router-dom';
import { mangaList } from '@/mocks/manga';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-600">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-950">漫画未找到</h2>
          <p className="mt-2 text-sm text-foreground-600">抱歉，您要查看的漫画不存在或已被移除。</p>
          <NavLink
            to="/"
            className="inline-block mt-6 px-6 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </NavLink>
        </div>
      </div>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.id !== manga.id && m.categorySlug === manga.categorySlug)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - ${manga.originalTitle} | 樱花动漫`,
    description: manga.description,
    image: manga.coverImage,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: manga.publishedAt,
    dateModified: manga.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '樱花动漫',
      url: 'https://sakuracomic.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://sakuracomic.cn/article/${manga.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sakuracomic.cn' },
      { '@type': 'ListItem', position: 2, name: manga.category, item: `https://sakuracomic.cn/category/${manga.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  const mergedSchema = { ...articleSchema, ...breadcrumbSchema };

  return (
    <>
      <SEOHead
        title={`${manga.title}_${manga.originalTitle}_在线免费阅读_樱花动漫`}
        description={manga.description}
        keywords={`樱花动漫,${manga.title},${manga.originalTitle},${manga.category},在线漫画,日本漫画`}
        canonicalUrl={`https://sakuracomic.cn/article/${manga.id}`}
        ogType="article"
        ogImage={manga.coverImage}
        schema={mergedSchema}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: manga.category, path: `/category/${manga.categorySlug}` },
              { label: manga.title },
            ]}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="flex-shrink-0">
            <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mx-auto lg:mx-0">
              <img
                src={manga.coverImage}
                alt={`${manga.title} 漫画封面`}
                title={`${manga.title} - 日本漫画在线阅读`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              {manga.title}
            </h1>
            <p className="text-sm text-foreground-600 mt-1">{manga.originalTitle}</p>

            <div className="flex flex-wrap items-center gap-4 mt-4">
              <span className="flex items-center gap-1 text-sm">
                <span className="text-yellow-500">
                  <i className="ri-star-fill text-sm"></i>
                </span>
                <strong className="text-foreground-950">{manga.rating}</strong>
                <span className="text-foreground-600">/ 10</span>
              </span>
              <span className="text-sm text-foreground-600 whitespace-nowrap">
                <i className="ri-eye-line mr-1"></i>
                {(manga.views / 10000).toFixed(0)}万 次阅读
              </span>
              <span className="text-sm text-foreground-600 whitespace-nowrap">
                <i className="ri-file-list-3-line mr-1"></i>
                共 {manga.chapters} 话
              </span>
              <span className={`text-sm px-2.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                manga.status === '连载中'
                  ? 'bg-accent-100/80 text-accent-700'
                  : 'bg-background-200 text-foreground-600'
              }`}>
                {manga.status}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <span className="text-xs text-foreground-600 whitespace-nowrap">作者：</span>
              <span className="text-xs text-accent-600 font-medium whitespace-nowrap">{manga.author}</span>
              <span className="text-xs text-foreground-600 mx-1">|</span>
              <span className="text-xs text-foreground-600 whitespace-nowrap">分类：</span>
              <NavLink
                to={`/category/${manga.categorySlug}`}
                className="text-xs text-accent-600 font-medium hover:text-accent-700 whitespace-nowrap"
              >
                {manga.category}
              </NavLink>
              <span className="text-xs text-foreground-600 mx-1">|</span>
              <span className="text-xs text-foreground-600 whitespace-nowrap">标签：</span>
              {manga.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-600 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 p-5 bg-background-100 rounded-xl">
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
              <p className="text-sm text-foreground-700 leading-relaxed">{manga.description}</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-book-open-line"></i>
                开始阅读第1话
              </button>
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-heart-line"></i>
                收藏
              </button>
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">章节列表</h2>
          <div className="border border-background-200/70 rounded-xl overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto">
              {Array.from({ length: Math.min(manga.chapters, 50) }, (_, i) => manga.chapters - i).map((ch) => (
                <button
                  key={ch}
                  className="w-full flex items-center justify-between px-5 py-3 border-b border-background-200/70 last:border-b-0 hover:bg-background-100 transition-colors text-left cursor-pointer"
                >
                  <span className="text-sm text-foreground-700 whitespace-nowrap">
                    第 {ch} 话
                    {ch === manga.chapters && '（最新）'}
                    {ch === 1 && '（第1话）'}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-600">
                    阅读 <i className="ri-arrow-right-s-line"></i>
                  </span>
                </button>
              ))}
              {manga.chapters > 50 && (
                <div className="px-5 py-3 text-center text-sm text-foreground-600">
                  ... 还有 {manga.chapters - 50} 话 ...
                </div>
              )}
            </div>
          </div>
        </div>

        {relatedManga.length > 0 && (
          <div className="mt-12">
            <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-4">同类推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {relatedManga.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}